import React, { useState, useEffect } from "react";
import { Appointment, FollowUpStatusItem, Professional } from "../../../types";
import { appointmentsApi } from "../../../services/appointmentsApi";
import { professionalsApi } from "../../../services/professionalsApi";
import { servicesApi } from "../../../services/servicesApi";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import { indexFollowUpStatusByClientId } from "./CarExpiredFollowUpReminder";
import {
  getTodayInBrazil,
} from "../../../utils/dateUtils";
import { sortAppointmentsByDeliveryEndTime } from "../../../utils/appointmentUtils";
import { AppointmentsGridView } from "./AppointmentsGridView";
import { AppointmentsCalendarView } from "./AppointmentsCalendarView";
import { CreateAppointmentModal } from "./CreateAppointmentModal";
import { AdminPageHeader } from "../shared";
import {
  Card,
  EmptyState,
  Loading,
  ErrorState,
  HorizontalDatePicker,
  ConfirmationModal,
} from "../../shared";
import { Icons } from "../../shared/Icons";

export const AdminAppointments: React.FC = () => {
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;
  const isProfessionalCar = isProfessional && company?.category === "car";

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return (
      sessionStorage.getItem("adminAppointmentsSelectedDate") ||
      getTodayInBrazil()
    );
  });
  // For professional + car: always use today, ignore stored date
  const effectiveDate = isProfessionalCar ? getTodayInBrazil() : selectedDate;
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [preFilledAppointmentData, setPreFilledAppointmentData] = useState<{
    professionalId?: number;
    date?: string;
    time?: string;
  } | undefined>(undefined);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [reactivateConfirmationOpen, setReactivateConfirmationOpen] =
    useState(false);
  const [selectedAppointmentForCancel, setSelectedAppointmentForCancel] =
    useState<Appointment | null>(null);
  const [
    selectedAppointmentForReactivate,
    setSelectedAppointmentForReactivate,
  ] = useState<Appointment | null>(null);
  const [followUpByClientId, setFollowUpByClientId] = useState<
    Map<number, FollowUpStatusItem[]>
  >(() => new Map());

  // Fetch professionals for barbershop companies
  useEffect(() => {
    const fetchProfessionals = async () => {
      if (!company || company.category !== "barbershop") return;

      try {
        // If user is a professional, fetch only their own data
        if (isProfessional && loggedInProfessionalId) {
          const response = await professionalsApi.getProfessionalById(loggedInProfessionalId);
          if (response.success && response.data) {
            setProfessionals([response.data]);
          }
        } else {
          // If user is company admin, fetch all professionals
          const response = await professionalsApi.getProfessionalsByCompany();
          if (response.success && response.data) {
            setProfessionals(response.data.filter((p) => p.isActive !== false));
          }
        }
      } catch (error) {
        console.error("Failed to fetch professionals:", error);
      }
    };

    fetchProfessionals();
  }, [company, isProfessional, loggedInProfessionalId]);

  // Save selectedDate to sessionStorage whenever it changes (not for professional + car)
  useEffect(() => {
    if (!isProfessionalCar) {
      sessionStorage.setItem("adminAppointmentsSelectedDate", selectedDate);
    }
  }, [selectedDate, isProfessionalCar]);

  useEffect(() => {
    if (company) {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDate, company, professionals, isProfessional, loggedInProfessionalId]);

  useEffect(() => {
    if (company?.id && !isProfessional) {
      fetchFollowUpStatus();
    } else {
      setFollowUpByClientId(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, isProfessional]);

  const fetchFollowUpStatus = async () => {
    try {
      const res = await servicesApi.getCompanyFollowUpStatus();
      if (res.success && res.data) {
        setFollowUpByClientId(indexFollowUpStatusByClientId(res.data.clients));
      } else {
        setFollowUpByClientId(new Map());
      }
    } catch {
      setFollowUpByClientId(new Map());
    }
  };


  const fetchAppointments = async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      if (company.category === "barbershop") {
        // For barbershop, fetch appointments for professionals
        const allAppointments: Appointment[] = [];

        // If user is a professional, only fetch their appointments
        if (isProfessional && loggedInProfessionalId) {
          const response =
            await appointmentsApi.getAppointmentsByProfessionalAndDate(
              loggedInProfessionalId,
              selectedDate
            );
          if (response.success && response.data) {
            const professional = professionals.find(p => p.id === loggedInProfessionalId);
            const appointmentsWithProfessional = response.data.map((apt) => ({
              ...apt,
              professionalName: professional?.name || "",
            }));
            allAppointments.push(...appointmentsWithProfessional);
          }
        } else {
          // For company admin, fetch appointments for all professionals
          for (const professional of professionals) {
            const response =
              await appointmentsApi.getAppointmentsByProfessionalAndDate(
                professional.id,
                selectedDate
              );
            if (response.success && response.data) {
              // Add professional name to each appointment for display
              const appointmentsWithProfessional = response.data.map((apt) => ({
                ...apt,
                professionalName: professional.name,
              }));
              allAppointments.push(...appointmentsWithProfessional);
            }
          }
        }

        // Sort appointments by time
        allAppointments.sort((a, b) => a.startTime.localeCompare(b.startTime));
        setAppointments(allAppointments);
      } else {
        // For car companies, fetch all appointments by company
        const response = await appointmentsApi.getAppointmentsByCompanyAndDate(
          effectiveDate
        );
        if (response.success && response.data) {
          setAppointments(sortAppointmentsByDeliveryEndTime(response.data));
        } else {
          setError(response.error || "Falha ao carregar agendamentos");
        }
      }
    } catch (err) {
      setError("Falha ao carregar agendamentos");
    } finally {
      setLoading(false);
    }
  };

  const isAppointmentFinished = (appointment: Appointment) => {
    return appointment.status.toLowerCase() === "completed";
  };

  const formatCurrency = (price: string) => {
    return `R$ ${price}`;
  };


  const handleCancelClick = (appointment: Appointment) => {
    setSelectedAppointmentForCancel(appointment);
    setCancelConfirmationOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointmentForCancel) return;

    try {
      const response = await appointmentsApi.cancelAppointment(
        selectedAppointmentForCancel.id
      );

      if (response.success) {
        await fetchAppointments(); // Refresh appointments
        setCancelConfirmationOpen(false);
        setSelectedAppointmentForCancel(null);
      } else {
        setError(response.error || "Erro ao cancelar agendamento");
      }
    } catch (err) {
      console.error("Error canceling appointment:", err);
      setError("Erro ao cancelar agendamento");
    }
  };

  const handleCancelModalClose = () => {
    setCancelConfirmationOpen(false);
    setSelectedAppointmentForCancel(null);
  };

  const handleReactivateClick = (appointment: Appointment) => {
    setSelectedAppointmentForReactivate(appointment);
    setReactivateConfirmationOpen(true);
  };

  const handleReactivateConfirm = async () => {
    if (!selectedAppointmentForReactivate) return;

    try {
      const response = await appointmentsApi.reactivateAppointment(
        selectedAppointmentForReactivate.id
      );

      if (response.success) {
        await fetchAppointments(); // Refresh appointments
        setReactivateConfirmationOpen(false);
        setSelectedAppointmentForReactivate(null);
      } else {
        setError(response.error || "Erro ao reativar agendamento");
      }
    } catch (err) {
      console.error("Error reactivating appointment:", err);
      setError("Erro ao reativar agendamento");
    }
  };

  const handleReactivateModalClose = () => {
    setReactivateConfirmationOpen(false);
    setSelectedAppointmentForReactivate(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <AdminPageHeader
        title="Agendamentos"
        subtitle={`${appointments.length} agendamento${
          appointments.length !== 1 ? "s" : ""
        }`}
        icon={<Icons.Calendar sx={{ fontSize: 28 }} />}
        buttonIcon={<Icons.Add sx={{ fontSize: 20 }} />}
        buttonText={isProfessional && company?.category === "car" ? undefined : "Novo Agendamento"}
        onButtonClick={isProfessional && company?.category === "car" ? undefined : () => setCreateModalOpen(true)}
        rightContent={
          !isProfessionalCar ? (
            <div className="flex-1 max-w-2xl">
              <HorizontalDatePicker
                showBorder={false}
                selectedDate={selectedDate}
                disablePast={false}
                onDateChange={setSelectedDate}
                className="shadow-none border-0 bg-transparent"
              />
            </div>
          ) : undefined
        }
      />

      {/* Loading State */}
      {loading && (
        <Loading
          message="Carregando agendamentos..."
          size="lg"
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12"
        />
      )}
      {/* Error State */}
      {error && (
        <ErrorState
          message={error}
          title="Erro ao Carregar"
          variant="banner"
          onRetry={fetchAppointments}
          retryText="Tentar Novamente"
        />
      )}

      {/* Appointments Content */}
      {!loading && !error && (
        <div className="space-y-6">
          {/* Calendar View for Barbershop */}
          {company?.category === "barbershop" ? (
            <Card className="overflow-hidden">
              <AppointmentsCalendarView
                professionals={professionals}
                selectedDate={selectedDate}
                onSlotClick={(slot) => {
                  setPreFilledAppointmentData({
                    professionalId: slot.professionalId,
                    date: slot.date,
                    time: slot.time,
                  });
                  setCreateModalOpen(true);
                }}
                onRefresh={fetchAppointments}
                followUpByClientId={
                  isProfessional ? undefined : followUpByClientId
                }
                onFollowUpScheduled={fetchFollowUpStatus}
              />
            </Card>
          ) : (
            /* Grid/List View for Car Companies */
            <>
              {appointments.length === 0 ? (
                <Card>
                  <EmptyState
                    icon="📅"
                    title="Nenhum agendamento encontrado"
                    description="Não há agendamentos para esta data."
                  />
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <AppointmentsGridView
                    appointments={appointments}
                    company={company}
                    isAppointmentFinished={isAppointmentFinished}
                    formatCurrency={formatCurrency}
                    onRefresh={fetchAppointments}
                    onCancel={isProfessionalCar ? undefined : handleCancelClick}
                    onReactivate={isProfessionalCar ? undefined : handleReactivateClick}
                    minimalView={isProfessionalCar}
                    followUpByClientId={
                      isProfessional ? undefined : followUpByClientId
                    }
                    onFollowUpScheduled={fetchFollowUpStatus}
                  />
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Create Appointment Modal */}
      {createModalOpen && (
        <CreateAppointmentModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setPreFilledAppointmentData(undefined);
          }}
          onSuccess={() => {
            setCreateModalOpen(false);
            setPreFilledAppointmentData(undefined);
            fetchAppointments();
          }}
          selectedDate={selectedDate}
          preFilledData={preFilledAppointmentData}
        />
      )}

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={cancelConfirmationOpen}
        title="Cancelar Agendamento"
        message={`Tem certeza que deseja cancelar o agendamento de ${selectedAppointmentForCancel?.client.name}? Esta ação não pode ser desfeita.`}
        confirmText="Cancelar Agendamento"
        cancelText="Voltar"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
      />

      {/* Reactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={reactivateConfirmationOpen}
        title="Reativar Agendamento"
        message={`Tem certeza que deseja reativar o agendamento de ${selectedAppointmentForReactivate?.client.name}? O agendamento voltará ao status de agendado.`}
        confirmText="Reativar Agendamento"
        cancelText="Voltar"
        onConfirm={handleReactivateConfirm}
        onCancel={handleReactivateModalClose}
        variant="success"
      />
    </div>
  );
};
